<template>
  <BaseToggle
    v-model:is-checked="isMounted"
    :label="label"
    test-id="failedMountAttempt"
    :is-inverted="true"
  />
  <BaseDropdown
    v-if="!isMounted"
    :selection="failedInstallationAttempt.reason"
    label="Grund für keine Montage"
    dropdown-text="Grund wählen"
    test-id="failedMountAttemptReason"
    :options-list="failedInstallationAttemptReasons"
    :validation="v$.failedInstallationAttempt.reason"
    :numbered="true"
    @update:selection="handleReasonChange"
  />
  <BaseRadioGroup
    v-if="!isMounted && failedInstallationAttempt.reason"
    v-model:selection="failedInstallationAttempt.type"
    class="mb-3 flex"
    data-testid="failedMountAttemptType"
    :options-list="impedimentTypes"
    :validation="v$.failedInstallationAttempt.type"
  />
  <BaseTextarea
    v-if="!isMounted"
    v-model:input-text="failedInstallationAttempt.note"
    test-id="failedMountAttemptNote"
    label="Bemerkung"
    class="mb-3"
    :validation="v$.failedInstallationAttempt.note"
    :max-length="NOTE_MAX_LENGTH"
  />
  <ImpedimentHistory
    v-if="!isMounted"
    class="pt-3"
    :entries="installationPoint.failedInstallationAttempts ?? []"
  />
  <BaseSection v-if="!isMounted" heading="Foto">
    <BaseInformation
      information-text="Zur Veranschaulichung des Grundes, weshalb das Gerät nicht montiert werden kann, nehmen Sie bitte ein Foto auf."
    />
    <Checkbox
      v-model:checked="isImageNotAllowedChecked"
      label="Nutzer verweigert Foto"
      checkbox-id="failedInstallationAttemptCheckbox"
    />
    <ImageCapture
      v-if="!isMounted"
      ref="imageCaptureRef"
      class="mt-4"
      :object-id="failedInstallationAttempt.id"
      :object-type="ImageObjectType.UNMOUNTED_DEVICE"
    />
  </BaseSection>
</template>

<script setup lang="ts">
import { computed, Ref, ref, WritableComputedRef } from 'vue';
import {
  deviceTypes,
  notMountedReasonsList,
  notMountedReasonsListSRT,
} from '@/enums/device';
import { impedimentTypes, NOTE_MAX_LENGTH } from '@/enums/generic';
import BaseToggle from '@/components/UI/Switch/BaseToggle.vue';
import BaseRadioGroup from '@/components/UI/Radio/BaseRadioGroup.vue';
import { useValidation } from '@/composables/useValidation';
import {
  FailedInstallationAttempt,
  InstallationPoint,
} from '@/models/installationPoint/InstallationPoint';
import BaseDropdown from '@/components/UI/Dropdown/BaseDropdown.vue';
import BaseTextarea from '@/components/UI/Note/BaseTextarea.vue';
import { FormAction, useFormStore } from '@/store/form/formStore';
import Checkbox from '@/components/UI/Checkbox/Checkbox.vue';
import BaseInformation from '@/components/UI/Hint/BaseInformation.vue';
import BaseSection from '@/components/UI/Card/BaseSection.vue';
import ImageCapture, {
  ImageCaptureRef,
} from '@/components/Forms/DeviceFormComponents/ImageCapture.vue';
import { ImageObjectType } from '@/models/Image';
import ImpedimentHistory from '@/components/ImpedimentHistory.vue';

const props = withDefaults(
  defineProps<{
    failedInstallationAttempt: FailedInstallationAttempt;
    isMounted: boolean;
    deviceType: string;
    installationPoint: InstallationPoint;
  }>(),
  {}
);

const isImageNotAllowedChecked = ref<boolean>(false);

const emit = defineEmits([
  'update:failedInstallationAttempt',
  'update:isMounted',
]);

const imageCaptureRef: Ref<ImageCaptureRef | null> = ref(null);

const failedInstallationAttemptReasons = computed(() => {
  if (props.deviceType === deviceTypes.SmartRadiatorThermostat) {
    return notMountedReasonsListSRT;
  }
  return notMountedReasonsList;
});

const failedInstallationAttempt: WritableComputedRef<FailedInstallationAttempt> =
  computed({
    get() {
      return props.failedInstallationAttempt;
    },
    set(value) {
      emit('update:failedInstallationAttempt', value);
    },
  });

const isMounted: WritableComputedRef<boolean> = computed({
  get() {
    return props.isMounted;
  },
  set(value) {
    emit('update:isMounted', value);
  },
});

const label = computed(() => {
  return useFormStore().formAction === FormAction.REPLACE
    ? 'Kein Austausch möglich'
    : 'Keine Montage möglich';
});

const handleReasonChange = (reason: string) => {
  failedInstallationAttempt.value.reason = reason;
  failedInstallationAttempt.value.type = undefined;
};

const { v$, isFormValid } = useValidation(
  { failedInstallationAttempt },
  { requiresImpedimentType: true }
);

defineExpose({
  isFormValid: computed(
    () =>
      isFormValid.value &&
      (imageCaptureRef.value?.hasImage || isImageNotAllowedChecked.value)
  ),
  saveImage: () => imageCaptureRef.value?.saveImage(),
});
</script>
